import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import { getInboundMerchant } from 'apis/merchant-onboarding-service/api';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T, typographyCss } from 'common/typography';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-inline: auto;
  padding-inline: 16px;
  padding-block: 40px;
  width: 100%;
  max-width: 621px;
`;

const MiddleImageContainer = styled.div`
  height: 100%;
  width: 227px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-inline: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 137px;
  }
`;

const MiddleImage = styled.img`
  object-fit: cover;
  object-position: center;
  height: 115%;
  width: auto;
  margin-inline-end: -25%;
`;

const SideImageContainer = styled.div`
  height: 233px;
  width: 175px;
  overflow: hidden;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  border-radius: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 138px;
    width: 98px;
  }
`;

const SideImage = styled.img`
  object-fit: cover;
  object-position: center;
  height: 100%;
  width: auto;
  border-radius: 8px;
  margin-block-start: -20%;
`;

const LeftImage = styled(SideImage)`
  height: 160%;
  margin-inline-end: -60%;
`;

const RightImage = styled(SideImage)`
  margin-block-start: -30%;
  height: 180%;
`;

const CarouselContainer = styled.div`
  height: 303px;
  display: flex;
  align-items: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 183px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 480px;
  margin-block-start: 32px;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
  variant: 'blue',
})`
  width: 100%;
`;

const Heading = styled.h2`
  margin-block-start: 0;
  margin-block-end: 16px;
  ${typographyCss.Heading2}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Heading4}
  }
`;

const Subheading = styled(T.Body3)`
  text-align: center;
  margin-block-end: 28px;
  padding-inline: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline: 10px;
  }
`;

const WelcomeView = () => {
  const history = useHistory();
  const [{ merchantId }] = useQueryParams({ merchantId: StringParam });

  const { isLoading: isInboundMerchandLoading, data: inboundMerchant } = useQuery(
    ['/merchant-admin/inbound-merchants', merchantId],
    () => {
      if (!merchantId) {
        throw new Error('No merchantId provided in query params.');
      }
      return getInboundMerchant(merchantId);
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  if (isInboundMerchandLoading || !inboundMerchant) {
    return <Spinner size="large" />;
  }

  return (
    <Wrapper>
      <CarouselContainer>
        <SideImageContainer>
          <LeftImage src={`${process.env.CDN_URL}/onboarding/ssmo/welcome-left.png`} alt="carousel image one" />
        </SideImageContainer>

        <MiddleImageContainer>
          <MiddleImage src={`${process.env.CDN_URL}/onboarding/ssmo/welcome-middle.png`} alt="carousel image two" />
        </MiddleImageContainer>

        <SideImageContainer>
          <RightImage src={`${process.env.CDN_URL}/onboarding/ssmo/welcome-right.png`} alt="carousel image three" />
        </SideImageContainer>
      </CarouselContainer>

      <ContentContainer>
        <Heading>Welcome, {inboundMerchant.firstName}!</Heading>

        <Subheading>
          Get ready to grow together by partnering with Wolt for Merchants. Start by completing your business
          information, and we’ll take it from there.
        </Subheading>

        <StyledButton
          onClick={() => history.push(`/onboarding-wizard?merchantId=${merchantId}&country=${inboundMerchant.country}`)}
        >
          Let’s start
        </StyledButton>
      </ContentContainer>
    </Wrapper>
  );
};

export default WelcomeView;
